import { uiText, useUiLanguage } from '../../../../utils/uiTranslation';
import React, { useMemo, useState } from 'react';
import RecruitmentFilterBar from '../AnalyticsRecruitment/components/RecruitmentFilterBar';
import WidgetShell from '../AnalyticsRecruitment/components/WidgetShell';
import WidgetLibraryModal from '../AnalyticsRecruitment/components/WidgetLibraryModal';
import pipelineRiskAnalyticsLibrary from './analyticsLibrary';
import RequisitionRiskModal from './components/RequisitionRiskModal';
import { getAnalyticsScreenDefaults, ANALYTICS_SCREEN_IDS } from '../../analyticsScreenDefaults';
import { getAnalyticsToolbarActions } from '../../analyticsToolbar';
import {
  canUseMaterializedAnalyticsBundle,
  getSupportedAnalyticsFilterState,
  getLocalizedGroupByOptions
} from '../../analyticsFilters';
import {
  createUnavailableAnalyticsBundle,
  mergeAnalyticsWidgetNode,
  useQueriedAnalyticsWidgetData,
  useAnalyticsWidgetData,
  useSharedAnalyticsDashboard
} from '../../analyticsRtdb';
import { createVisualModel, ANALYTICS_DATA_STATUS } from '../../visuals/visualDataModel';
import { ANALYTICS_GRAPH_TYPES } from '../../visuals/graphCatalog';
import { getCompatibleGraphs, resolveVisualRenderer } from '../../visuals/visualRegistry';
import UnavailableVisual from '../../visuals/UnavailableVisual';
import useCanonicalAnalyticsExport from '../../useCanonicalAnalyticsExport';

const ADJACENT_WIDGETS = {
  stage_bottleneck: 'offer_stalling',
  offer_stalling: 'stage_bottleneck'
};
const SCREEN_DEFAULTS = getAnalyticsScreenDefaults(ANALYTICS_SCREEN_IDS.PIPELINE_RISK);
const BACKEND_WIDGET_IDS = new Set([
  'open_roles_risk',
  'stage_bottleneck',
  'offer_stalling',
  'vacancy_forecast',
  'candidate_dropoff_risk'
]);
const LIVE_REFRESH_WIDGET_IDS = new Set(BACKEND_WIDGET_IDS);
const PIPELINE_RISK_QUERY_REVISION = 'pipeline-risk-live-metrics-2026-05-16-r4';

const COPY = {
  en: {
    library: {
      title: 'Widget Library',
      subtitle: 'Pipeline & Risk ships with five widgets only. Each one is focused on what is breaking now and what happens next if nothing changes.',
      close: 'Close',
      add: 'Add widget',
      added: 'Added',
      exampleDataReady: 'Data available',
      exampleDataMissing: 'Not available yet',
      graphCount: (count) => `${count} graph${count === 1 ? '' : 's'}`,
      categories: {
        pipeline_risk_core: 'Pipeline & Risk'
      }
    },
    menu: {
      settings: 'Widget settings',
      chartType: 'Chart type',
      groupBy: 'Group by',
      cardSize: 'Card size',
      resize: 'Resize widget',
      compact: 'Compact',
      medium: 'Medium',
      'three-quarter': 'Three quarter',
      large: 'Large',
      unavailable: 'No compatible data is available for this graph yet.',
      loading: 'Loading analytics data...',
      loadFailed: 'Could not load analytics data.',
      graphs: {
        [ANALYTICS_GRAPH_TYPES.BUBBLE_CHART]: 'Bubble chart',
        [ANALYTICS_GRAPH_TYPES.HEATMAP]: 'Heatmap',
        [ANALYTICS_GRAPH_TYPES.GANTT_TIMELINE]: 'Gantt timeline',
        [ANALYTICS_GRAPH_TYPES.STALLING_OFFERS]: 'Offer stalling',
        [ANALYTICS_GRAPH_TYPES.RISK_ACTION_TABLE]: 'Risk table'
      }
    },
    detailClose: 'Close'
  },
  ar: {
    library: {
      title: 'مكتبة العناصر',
      subtitle: 'واجهة المسار والمخاطر تنطلق بخمسة عناصر فقط، وكل عنصر يجيب مباشرة عمّا يتعطل الآن وما الذي سيحدث إذا لم يتغير شيء.',
      close: 'إغلاق',
      add: 'إضافة عنصر',
      added: 'مضاف',
      exampleDataReady: 'البيانات التجريبية جاهزة',
      exampleDataMissing: 'البيانات غير متاحة',
      graphCount: (count) => `${count} رسم`,
      categories: {
        pipeline_risk_core: 'المسار والمخاطر'
      }
    },
    menu: {
      settings: 'إعدادات العنصر',
      chartType: 'نوع الرسم',
      groupBy: 'التجميع حسب',
      cardSize: 'حجم البطاقة',
      resize: 'تغيير حجم العنصر',
      compact: 'مصغر',
      medium: 'متوسط',
      'three-quarter': 'ثلاثة أرباع',
      large: 'كبير',
      unavailable: 'لا توجد بيانات متوافقة لهذا الرسم حتى الآن.',
      loading: 'يتم تحميل بيانات التحليلات...',
      loadFailed: 'تعذر تحميل بيانات التحليلات.',
      graphs: {
        [ANALYTICS_GRAPH_TYPES.BUBBLE_CHART]: 'مخطط فقاعي',
        [ANALYTICS_GRAPH_TYPES.HEATMAP]: 'خريطة حرارية',
        [ANALYTICS_GRAPH_TYPES.GANTT_TIMELINE]: 'خط زمني جانت',
        [ANALYTICS_GRAPH_TYPES.STALLING_OFFERS]: 'تعثر العروض',
        [ANALYTICS_GRAPH_TYPES.RISK_ACTION_TABLE]: 'جدول المخاطر'
      }
    },
    detailClose: 'إغلاق'
  }
};

const WidgetLoadingVisual = ({ message }) => { useUiLanguage(); return (<div className="analytics-visual analytics-visual-unavailable analytics-visual-loading">
    <i className="fa-solid fa-spinner fa-spin" />
    <span>{uiText(message)}</span>
  </div>); };

const AnalyticsPipelineRisk = ({ isRTL = false, companyUID = '', analyticsActorId = 'system' }) => {
  const copy = isRTL ? COPY.ar : COPY.en;
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [activeRequisitionId, setActiveRequisitionId] = useState(null);
  const filterState = useMemo(() => ({}), []);
  const {
    visibleWidgetIds,
    widgetPrefs,
    mutateDashboardState,
    replaceDashboardState,
    resetDashboardState,
    dashboardReady
  } = useSharedAnalyticsDashboard({
    companyUID,
    screenId: SCREEN_DEFAULTS.screenId,
    defaultVisibleWidgetIds: SCREEN_DEFAULTS.defaultVisibleWidgetIds,
    defaultWidgetPrefs: SCREEN_DEFAULTS.defaultWidgetPrefs,
    locked: SCREEN_DEFAULTS.locked,
      updatedBy: analyticsActorId
  });
  const { widgetNodes, loading: widgetLoading } = useAnalyticsWidgetData({
    companyUID,
    screenId: SCREEN_DEFAULTS.screenId,
    widgetIds: dashboardReady ? visibleWidgetIds : []
  });
  const localizedGroupByOptions = useMemo(() => getLocalizedGroupByOptions(isRTL), [isRTL]);

  const widgetEntries = useMemo(
    () => visibleWidgetIds.map((id) => pipelineRiskAnalyticsLibrary.widgets.find((widget) => widget.id === id)).filter(Boolean),
    [visibleWidgetIds]
  );

  const widgetRuntimeConfig = useMemo(
    () =>
      Object.fromEntries(
        widgetEntries.map((entry) => {
          const prefs = widgetPrefs[entry.id] || {};
          const activeGroupBy = prefs.selectorState?.groupBy || entry.defaultGroupBy || '';
          const effectiveFilters = getSupportedAnalyticsFilterState({
            filterState,
            supportedFilters: entry.supportedFilters || [],
            groupBy: activeGroupBy
          });
          const materializedGroupBy = Array.isArray(widgetNodes[entry.id]?.supportedGroupBy)
            ? widgetNodes[entry.id].supportedGroupBy
            : [];
          const canUseMaterializedBundle = canUseMaterializedAnalyticsBundle({
            screenId: SCREEN_DEFAULTS.screenId,
            filterState,
            supportedFilters: entry.supportedFilters || [],
            supportsGroupBy: entry.supportsGroupBy,
            activeGroupBy,
            defaultGroupBy: entry.defaultGroupBy || '',
            materializedGroupBy
          });

          return [entry.id, {
            prefs,
            activeGroupBy,
            effectiveFilters,
            canUseMaterializedBundle
          }];
        })
      ),
    [filterState, widgetEntries, widgetNodes, widgetPrefs]
  );

  const { widgetNodes: queriedWidgetNodes, loading: queriedLoading } = useQueriedAnalyticsWidgetData({
    companyUID,
    screenId: SCREEN_DEFAULTS.screenId,
    widgetQueryMap: Object.fromEntries(
      widgetEntries.map((entry) => {
        const runtime = widgetRuntimeConfig[entry.id] || {};
        const hasMaterializedNode = Boolean(widgetNodes[entry.id]);
        const shouldRefreshLive = LIVE_REFRESH_WIDGET_IDS.has(entry.id);
        return [entry.id, {
          enabled: Boolean(companyUID) && (
            shouldRefreshLive ||
            !runtime.canUseMaterializedBundle ||
            !hasMaterializedNode
          ),
          filters: runtime.effectiveFilters || {},
          groupBy: runtime.activeGroupBy || '',
          revision: shouldRefreshLive ? `${PIPELINE_RISK_QUERY_REVISION}:${entry.id}` : 0
        }];
      })
    )
  });
  const widgetLibraryCategories = useMemo(
    () =>
      pipelineRiskAnalyticsLibrary.categories.map((category) => ({
        id: category.id,
        label: copy.library.categories[category.id] || category.label,
        widgets: category.widgets.map((widget) => ({
          id: widget.id,
          label: widget.label,
          defaultGraphLabel: copy.menu.graphs[widget.defaultGraph] || widget.defaultGraph,
          graphCount: widget.supportedGraphs.length,
          isAdded: visibleWidgetIds.includes(widget.id),
          hasReadyData: Boolean(widgetNodes[widget.id] || queriedWidgetNodes[widget.id])
        }))
      })),
    [copy.library.categories, copy.menu.graphs, queriedWidgetNodes, visibleWidgetIds, widgetNodes]
  );
  const isScreenLoading =
    Boolean(companyUID) &&
    (!dashboardReady ||
      ((widgetLoading || queriedLoading) &&
        !Object.keys(widgetNodes).length &&
        !Object.keys(queriedWidgetNodes).length));

  const widgetModels = useMemo(
    () =>
      widgetEntries.map((entry) => {
        const runtime = widgetRuntimeConfig[entry.id] || {};
        const prefs = runtime.prefs || widgetPrefs[entry.id] || {};
        const activeGroupBy = runtime.activeGroupBy || entry.defaultGroupBy || '';
        const unavailableBundle = createUnavailableAnalyticsBundle({
          title: entry.label,
          annotationItems: [],
          supportedFilters: entry.supportedFilters || [],
          supportedGroupBy: entry.groupByOptions || []
        });
        const hasMaterializedNode = Boolean(widgetNodes[entry.id]);
        const canUseMaterializedBundle = Boolean(runtime.canUseMaterializedBundle) && hasMaterializedNode;
        const forceQueriedBundle = LIVE_REFRESH_WIDGET_IDS.has(entry.id);
        const queryHasResolved = Object.prototype.hasOwnProperty.call(queriedWidgetNodes, entry.id);
        const queryNode = queriedWidgetNodes[entry.id];
        const isQueryBackfill = Boolean(companyUID) && BACKEND_WIDGET_IDS.has(entry.id) && (!canUseMaterializedBundle || forceQueriedBundle);
        const isLoading = isQueryBackfill && queriedLoading && !queryHasResolved;
        const queryFailed = isQueryBackfill && queryHasResolved && !queryNode;
        const bundle = !forceQueriedBundle && canUseMaterializedBundle
          ? mergeAnalyticsWidgetNode(widgetNodes[entry.id], unavailableBundle, {
              selectedViewId: entry.supportsGroupBy ? activeGroupBy : '', isRTL
            })
          : queryNode
            ? mergeAnalyticsWidgetNode(queryNode, unavailableBundle, {
              selectedViewId: entry.supportsGroupBy ? activeGroupBy : '', isRTL
            })
            : companyUID && BACKEND_WIDGET_IDS.has(entry.id)
              ? unavailableBundle
              : unavailableBundle;
        const availableGraphIds = getCompatibleGraphs(entry, bundle, prefs.size || entry.defaultSize);
        const activeGraph = availableGraphIds.includes(prefs.activeGraph) ? prefs.activeGraph : availableGraphIds[0] || '';
        const dataset = activeGraph ? bundle.datasets[activeGraph] : null;

        return createVisualModel({
          id: entry.id,
          type: entry.visualType,
          size: prefs.size || entry.defaultSize,
          variant: prefs.variant || entry.defaultVariant,
          datasets: bundle.datasets,
          datasetShape: dataset?.shape || '',
          dataset,
          activeGraph,
          availableGraphs: availableGraphIds.map((graphId) => ({
            id: graphId,
            label: copy.menu.graphs[graphId] || graphId
          })),
          dataStatus:
            bundle.dataStatus === ANALYTICS_DATA_STATUS.READY && dataset
              ? ANALYTICS_DATA_STATUS.READY
              : bundle.dataStatus,
          title: bundle.title || entry.label,
          annotations: bundle.annotationItems || [],
          meta: {
            dataSemantics: entry.dataSemantics,
            graphConfig: entry.graphConfig,
            activeGroupBy,
            supportedGroupBy: bundle.supportedGroupBy || entry.groupByOptions || [],
            isLoading,
            queryFailed
          },
          options: entry.id === 'open_roles_risk'
            ? { onSelectCategory: (roleId) => setActiveRequisitionId(roleId) }
            : entry.id === 'candidate_dropoff_risk'
              ? { onAction: () => {} }
            : {}
        });
      }),
    [companyUID, copy.menu.graphs, isRTL, queriedLoading, queriedWidgetNodes, widgetEntries, widgetNodes, widgetPrefs, widgetRuntimeConfig]
  );
  const {
    exporting,
    exportMode,
    handleExportAction
  } = useCanonicalAnalyticsExport({
    screenId: SCREEN_DEFAULTS.screenId,
    companyUID,
    analyticsActorId,
    filterState: { period: 'last_60_days', ...filterState },
    widgetModels,
    language: isRTL ? 'ar' : 'en'
  });
  const toolbarActions = useMemo(
    () => getAnalyticsToolbarActions({
      isRTL,
      locked: SCREEN_DEFAULTS.locked,
      allowExport: true,
      exporting,
      exportMode
    }),
    [exportMode, exporting, isRTL]
  );

  const handleAction = (actionId) => {
    if (handleExportAction(actionId)) {
      return;
    }

    if (actionId === 'add_widget') {
      setLibraryOpen(true);
      return;
    }
    if (actionId === 'reset_layout') {
      resetDashboardState();
    }
  };

  const handleAddWidget = (widgetId) => {
    if (visibleWidgetIds.includes(widgetId)) return;
    const entry = pipelineRiskAnalyticsLibrary.widgets.find((widget) => widget.id === widgetId);
    replaceDashboardState(
      [...visibleWidgetIds, widgetId],
      {
        ...widgetPrefs,
        [widgetId]: {
          size: entry?.defaultSize || 'medium',
          activeGraph: entry?.defaultGraph || ''
        }
      }
    );
  };

  const updateGraphType = (widgetId, graphId) => {
    mutateDashboardState((current) => ({
      ...current,
      widgetPrefs: {
        ...current.widgetPrefs,
        [widgetId]: {
          ...current.widgetPrefs[widgetId],
          activeGraph: graphId
        }
      }
    }));
  };

  const updateGroupBy = (widgetId, nextGroupBy) => {
    mutateDashboardState((current) => ({
      ...current,
      widgetPrefs: {
        ...current.widgetPrefs,
        [widgetId]: {
          ...(current.widgetPrefs[widgetId] || {}),
          selectorState: {
            ...(current.widgetPrefs[widgetId]?.selectorState || {}),
            groupBy: nextGroupBy
          }
        }
      }
    }));
  };

  const updateSize = (widgetId, size) => {
    mutateDashboardState((current) => {
      const nextWidgetPrefs = {
        ...current.widgetPrefs,
        [widgetId]: {
          ...current.widgetPrefs[widgetId],
          size
        }
      };

      const adjacentWidgetId = ADJACENT_WIDGETS[widgetId];
      if (size === 'three-quarter' && adjacentWidgetId && nextWidgetPrefs[adjacentWidgetId]) {
        nextWidgetPrefs[adjacentWidgetId] = {
          ...nextWidgetPrefs[adjacentWidgetId],
          size: 'compact'
        };
      }

      return {
        ...current,
        widgetPrefs: nextWidgetPrefs
      };
    });
  };

  const activeRequisitionDetail = useMemo(() => {
    if (!activeRequisitionId) return null;
    return null;
  }, [activeRequisitionId]);

  return (
    <section className="analytics-recruitment-view">
      {isScreenLoading ? (
        <div className="data-analytics-loading-screen">
          <i className="fa-solid fa-spinner fa-spin" />
          <p>{isRTL ? 'يرجى الانتظار' : 'Please Wait'}</p>
        </div>
      ) : (
        <>
      <RecruitmentFilterBar
        filters={[]}
        actions={toolbarActions}
        onAction={handleAction}
        onFilterChange={() => {}}
        isRTL={isRTL}
      />

      <div className="analytics-recruitment-canvas">
        {widgetModels.map((model) => {
          const Renderer = model.dataset ? resolveVisualRenderer(model.activeGraph, model.datasetShape) : null;
          const groupByOptions = localizedGroupByOptions.filter((option) =>
            (model.meta.supportedGroupBy || []).includes(option.id)
          );

          return (
            <WidgetShell
              key={model.id}
              id={model.id}
              title={model.title}
              className={`analytics-recruitment-widget-${model.id}`}
              size={model.size}
              variant={model.variant}
              chartTypes={model.availableGraphs}
              activeChartType={model.activeGraph}
              groupByOptions={groupByOptions}
              activeGroupBy={model.meta.activeGroupBy}
              annotationItems={model.annotations}
              onChartTypeChange={updateGraphType}
              onGroupByChange={updateGroupBy}
              onSizeChange={updateSize}
              menuCopy={copy.menu}
              isRTL={isRTL}
              pairedCompact={widgetPrefs[ADJACENT_WIDGETS[model.id]]?.size === 'compact'}
            >
              {model.meta.isLoading ? (
                <WidgetLoadingVisual message={copy.menu.loading} />
              ) : model.dataStatus !== ANALYTICS_DATA_STATUS.READY || !Renderer ? (
                <UnavailableVisual message={model.meta.queryFailed ? copy.menu.loadFailed : copy.menu.unavailable} />
              ) : (
                <Renderer model={model} />
              )}
            </WidgetShell>
          );
        })}
      </div>

      <WidgetLibraryModal
        isOpen={libraryOpen}
        copy={copy.library}
        categories={widgetLibraryCategories}
        onClose={() => setLibraryOpen(false)}
        onAddWidget={handleAddWidget}
        isRTL={isRTL}
      />

      <RequisitionRiskModal
        detail={activeRequisitionDetail}
        onClose={() => setActiveRequisitionId(null)}
        closeLabel={copy.detailClose}
        isRTL={isRTL}
      />
        </>
      )}
    </section>
  );
};

export default AnalyticsPipelineRisk;
